<template>
  <header class="complete-checkpoint-header">
    <div
      class="complete-checkpoint-header__top"
      :class="{ 'complete-checkpoint-header__top--close-left': closeOnLeft }"
    >
      <div class="complete-checkpoint-header__content">
        <h2 class="complete-checkpoint-header__title">
          {{ title }}
        </h2>

        <template v-if="isCoachStyle">
          <template v-if="showResponsibilityChip">
            <span class="complete-checkpoint-header__title">on responsibility</span>
            <span class="complete-checkpoint-header__chip" data-testid="header-responsibility-chip">
              <span
                v-if="responsibilityCode"
                class="complete-checkpoint-header__chip-code"
                data-testid="header-responsibility-code"
              >
                {{ responsibilityCode }}
              </span>
              <span
                v-if="responsibilityName"
                class="complete-checkpoint-header__chip-name"
                data-testid="header-responsibility-name"
              >
                {{ responsibilityName }}
              </span>
            </span>
          </template>
        </template>

        <div v-else-if="showEmployee" class="complete-checkpoint-header__employee">
          <ExAvatar :picture="avatar" :firstName="firstName" :lastName="lastName" size="medium" />
          <span v-if="displayName" class="complete-checkpoint-header__employee-name">
            {{ displayName }}
          </span>
          <span v-if="jobTitle" class="complete-checkpoint-header__employee-title">
            {{ jobTitle }}
          </span>
          <span
            v-if="responsibilityName"
            class="complete-checkpoint-header__responsibility"
            data-testid="header-responsibility"
          >
            {{ responsibilityName }}
          </span>
        </div>
      </div>

      <button
        type="button"
        class="complete-checkpoint-header__close"
        :class="{ 'complete-checkpoint-header__close--left': closeOnLeft }"
        aria-label="Close"
        @click="emit('close')"
      >
        <Icon name="popup_close" color="dark" size="normal" />
      </button>
    </div>

    <div v-if="isCoachStyle" class="complete-checkpoint-header__criteria-row">
      <InspectionLevelBadge :level="level" />
      <span class="complete-checkpoint-header__criteria-label">Checkpoint criteria</span>
      <p
        v-if="criteria"
        class="complete-checkpoint-header__criteria-banner"
        data-testid="header-criteria-banner"
        :title="criteria"
      >
        {{ criteria }}
      </p>
    </div>

    <div v-else class="complete-checkpoint-header__banner">
      <InspectionLevelBadge :level="level" />
      <p v-if="criteria" class="complete-checkpoint-header__criteria" :title="criteria">
        {{ criteria }}
      </p>
    </div>
  </header>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import Icon from "@components/icons/Icon.vue";
import InspectionLevelBadge from "@components/InspectionLevelBadge/InspectionLevelBadge.vue";

const props = defineProps({
  /** Pane title shown in the top bar. */
  title: {
    type: String,
    default: "Complete Checkpoint",
  },
  /** Employee display name (e.g. "Olivia Rhye"). */
  employeeName: {
    type: String,
    default: "",
  },
  firstName: {
    type: String,
    default: "",
  },
  lastName: {
    type: String,
    default: "",
  },
  /** Job title / role shown next to the name. */
  jobTitle: {
    type: String,
    default: "",
  },
  /** Profile photo URL or asset. */
  avatar: {
    type: [String, Object],
    default: null,
  },
  /** Readiness level for the pill badge. */
  level: {
    type: String,
    default: "Apprentice",
    validator: (value) => ["Apprentice", "Professional", "Coach", "Master"].includes(value),
  },
  /** Checkpoint criteria text shown in the gradient banner. */
  criteria: {
    type: String,
    default: "",
  },
  /** Responsibility being inspected, shown after the job title. */
  responsibilityName: {
    type: String,
    default: "",
  },
  /** Unique org code of the responsibility, shown before its name in the coach-style chip. */
  responsibilityCode: {
    type: String,
    default: "",
  },
  /** "classic" keeps the employee block + gradient banner; "coach-style" shows the responsibility chip + criteria row. */
  variant: {
    type: String,
    default: "classic",
    validator: (value) => ["classic", "coach-style"].includes(value),
  },
  /** Side of the header the close button sits on. */
  closePosition: {
    type: String,
    default: "right",
    validator: (value) => ["left", "right"].includes(value),
  },
});

const emit = defineEmits(["close"]);

const displayName = computed(() => {
  if (props.employeeName) return props.employeeName;
  return [props.firstName, props.lastName].filter(Boolean).join(" ");
});

const isCoachStyle = computed(() => props.variant === "coach-style");

const closeOnLeft = computed(() => isCoachStyle.value || props.closePosition === "left");

const showResponsibilityChip = computed(() =>
  Boolean(props.responsibilityCode || props.responsibilityName)
);

const showEmployee = computed(() => Boolean(displayName.value || props.jobTitle || props.avatar));
</script>

<style scoped>
.complete-checkpoint-header {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
}

.complete-checkpoint-header__top {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  padding: 0 80px 0 24px;
  box-sizing: border-box;
}

.complete-checkpoint-header__content {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  gap: 16px;
  min-width: 0;
  padding-top: 24px;
}

.complete-checkpoint-header__title {
  margin: 0;
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 30px;
  color: #101828;
  white-space: nowrap;
}

.complete-checkpoint-header__employee {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.complete-checkpoint-header__employee-name {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
  white-space: nowrap;
}

.complete-checkpoint-header__employee-title {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #475467;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.complete-checkpoint-header__top--close-left {
  padding: 0 24px 0 72px;
}

.complete-checkpoint-header__responsibility {
  flex-shrink: 0;
  padding-left: 8px;
  border-left: 1px solid #d0d5dd;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #475467;
  white-space: nowrap;
}

.complete-checkpoint-header__close {
  position: absolute;
  top: 12px;
  right: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  box-sizing: border-box;
}

.complete-checkpoint-header__close--left {
  right: auto;
  left: 16px;
}

.complete-checkpoint-header__close:hover {
  background: #f2f4f7;
}

.complete-checkpoint-header__close:focus {
  outline: 2px solid #98a2b3;
  outline-offset: 2px;
}

.complete-checkpoint-header__close:focus:not(:focus-visible) {
  outline: none;
}

.complete-checkpoint-header__banner {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 16px 10px 24px;
  overflow: hidden;
  box-sizing: border-box;
  background: linear-gradient(180deg, #a5c0ee 0%, #fbc5ec 100%);
}

.complete-checkpoint-header__criteria {
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  color: #101828;
}

.complete-checkpoint-header__chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid #d1d6de;
  border-radius: 8px;
  background: #ffffff;
  box-sizing: border-box;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 17px;
}

.complete-checkpoint-header__chip-code {
  flex-shrink: 0;
  font-weight: 400;
  color: #667085;
  white-space: nowrap;
}

.complete-checkpoint-header__chip-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 600;
  color: #101828;
}

.complete-checkpoint-header__criteria-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 16px 12px;
  box-sizing: border-box;
  background: #ffffff;
}

.complete-checkpoint-header__criteria-label {
  flex-shrink: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  color: #101828;
  white-space: nowrap;
}

.complete-checkpoint-header__criteria-banner {
  margin: 0;
  min-width: 0;
  flex: 1 1 auto;
  padding: 9px 12px;
  border: 1px solid #edf0f2;
  border-radius: 6px;
  background: #f9fafb;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 17px;
  color: #101828;
}
</style>
